
import { getTechniciansJobs, getTechniciansList, updateTechniciansJobs } from '@/api';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { User } from '@/models/main';
import { mapState } from 'vuex';
import { timeZoneDefault } from '@/utils/PortalConstants';
import { format, utcToZonedTime } from 'date-fns-tz';
import * as routeNames from '@/router/routeNames';
import DateRangeCustom from '@/components/searchPage/DateRangeCustom.vue';
import StopDrawing from '@/components/jobDetailed/StopDrawing/StopDrawing.vue';

import Header from '@/components/jobBooking/Header/Header.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/Button.vue';
import { TechnicianCatalogItem, TechniciansJobsResponseData } from '@/models/technicians';
import { notifyNetworkError, notifyNetworkSuccess } from '@/helpers/notifiers';
import { JobDetailedData, JobDetailedStop } from '@/models/jobDetailed';
import { addDays } from 'date-fns';

@Component({
  components: {
    BackButton,
    Button,
    DateRangeCustom,
    Header,
    StopDrawing,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class Technicians extends Vue {
  private user!: User;
  public routeNames = routeNames;
  public format = format;
  public utcToZonedTime = utcToZonedTime;
  get userTimeZone() {
    return this.user?.timezone || timeZoneDefault;
  }

  public loadingTechnicians = false;
  public loadingJobs = false;
  public loadingUpdateTechnicians = false;
  public technicians: TechnicianCatalogItem[] = [];
  public jobs: JobDetailedData[] = [];
  public draggingTechnician: TechnicianCatalogItem | null = null;
  public changedTechniciansList: { [key: number]: TechnicianCatalogItem | null } = {};
  public savedTechniciansList: { [key: number]: TechnicianCatalogItem | null } = {};

  private pageData: TechniciansJobsResponseData = {
    count: 0,
    next: '',
    previous: '',
    results: [],
  };
  private pagination = {
    page: 1,
    total: 0,
    perPage: 10,
    visible: 6,
    perPageOptions: [1, 5, 10, 50, 100],
  };

  public dates: string[] = [
    format(utcToZonedTime(addDays(new Date(), -13), this.userTimeZone), 'yyyy-MM-dd'),
    format(utcToZonedTime(new Date(), this.userTimeZone), 'yyyy-MM-dd'),
  ];

  /**
   * Checks if there are unsaved changes in the technicians list.
   *
   * Compares the current state of `changedTechniciansList` with `savedTechniciansList`
   * to determine if any modifications have been made that have not yet been saved.
   */
  public pageHaveUnsavedChanges() {
    return JSON.stringify(this.changedTechniciansList) !== JSON.stringify(this.savedTechniciansList);
  }

  public changeDate(dates: string[]) {
    this.dates = dates;
    this.changedTechniciansList = {};
    this.getJobListWrapper();
  }

  public onDragStart(event: DragEvent, technician: TechnicianCatalogItem) {
    this.draggingTechnician = technician;
  }

  public onDragEnd() {
    this.draggingTechnician = null;
  }

  /**
   * Handle a drop event
   * If the user is dragging a technician, record it in changedTechniciansList
   * We use the target id as the key, not the draggingTechnician id
   */
  public handleDrop(data: { target: JobDetailedStop }) {
    if (this.draggingTechnician == null) {
      return;
    }
    if (!this.changedTechniciansList[this.draggingTechnician.id]) {
      this.changedTechniciansList[data.target.id] = this.draggingTechnician;
      this.$forceUpdate();
    }
  }

  /**
   * Called when the user clicks on the 'X' button in the technician chip at a stop.
   * Clears the changedTechniciansList entry for the given stop id and updates the savedTechniciansList
   * if the stop was not set before
   * @param stop_id The id of the stop which the technician was assigned to
   */
  public handleChipClose(stop_id: number): void {
    if (this.changedTechniciansList[stop_id] !== null) {
      this.changedTechniciansList[stop_id] = null;
      if (!this.savedTechniciansList[stop_id]) {
        this.savedTechniciansList[stop_id] = null;
      }
      this.$forceUpdate();
    }
  }

  /**
   * Updates the pagination details for the job list.
   * It fetches the job list data, calculates the total number of pages based on
   * the total job count and items per page, and adjusts the current page
   * if it is out of bounds
   */
  private async updatePagination() {
    await this.getJobListWrapper();
    this.pagination.total = Math.ceil(this.pageData.count / Number(this.pagination.perPage));
    if (this.pagination.page === 0 && this.pagination.total >= 1) {
      this.pagination.page = 1;
    }
    if (this.pagination.page > this.pagination.total) {
      this.pagination.page = this.pagination.total;
    }
  }

  @Watch('pagination.perPage')
  private async perPageSelectChanged() {
    this.pagination.page = 1;
    this.pagination.perPage = Number(this.pagination.perPage);
    this.updatePagination();
  }

  public async updateTechnicians() {
    this.loadingUpdateTechnicians = true;
    const techs = Object.keys(this.changedTechniciansList).map(tech => {
      const id = this.changedTechniciansList[tech]?.id;
      return [tech, id ? id : null];
    });

    await updateTechniciansJobs(techs)
      .then(() => {
        notifyNetworkSuccess({ text: 'Technicians was successfully updated' });
        this.getJobListWrapper();
      })
      .catch(err => {
        notifyNetworkError({ title: 'Error loading technicians', text: err.response.data });
      })
      .finally(() => {
        this.loadingUpdateTechnicians = false;
      });
  }

  private async getTechniciansWrapper() {
    this.loadingTechnicians = true;
    await getTechniciansList()
      .then(res => {
        this.technicians = res.data;
      })
      .catch(() => {
        notifyNetworkError({ text: 'Error loading technicians' });
      })
      .finally(() => {
        this.loadingTechnicians = false;
      });
  }

  /**
   * Makes a request to API to get a list of jobs for given dates
   * and updates the page data. Also, it resets the changedTechniciansList
   * and savedTechniciansList and sets the pagination.total
   */
  private async getJobListWrapper() {
    this.loadingJobs = true;
    const endpointParams = {
      page: this.pagination.page,
      page_size: this.pagination.perPage,
    };
    await getTechniciansJobs(this.dates.join(','), endpointParams.page, endpointParams.page_size)
      .then(res => {
        this.pageData = res.data;
        this.changedTechniciansList = {};
        this.savedTechniciansList = {};
        this.pageData.results.map(job => {
          job.stops.map(stop => {
            if (stop.assigned_technician) {
              this.savedTechniciansList[stop.id] = stop.assigned_technician;
              this.changedTechniciansList = { ...this.savedTechniciansList };
            }
          });
        });
        this.pagination.total = Math.ceil(this.pageData.count / this.pagination.perPage);
      })
      .catch(() => {
        this.pagination.page = 1;
        this.pageData.results.length = 0;
        notifyNetworkError({ text: 'Error loading jobs' });
      })
      .finally(() => {
        this.loadingJobs = false;
      });
  }

  private mounted(): void {
    this.getTechniciansWrapper();
    this.getJobListWrapper();
  }
}
